import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  ALLOWED_CONTENT_TYPES,
  formatTimestamp,
  getInvoice,
  INVOICE_CATEGORIES,
  MAX_IMAGE_SIZE_BYTES,
  replaceInvoiceImage,
  updateInvoice,
  uploadToS3,
  type InvoiceCategory,
  type InvoiceWithImageUrl
} from '../lib/invoices';

export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState<InvoiceWithImageUrl | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [replacingImage, setReplacingImage] = useState(false);

  // Local form state mirrors invoice metadata so the staff member can edit it
  const [category, setCategory] = useState<InvoiceCategory>('Petrol');
  const [otherReason, setOtherReason] = useState('');

  const refresh = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const inv = await getInvoice(invoiceId);
      setInvoice(inv);
      setCategory(inv.category);
      setOtherReason(inv.otherReason ?? '');
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [invoiceId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (error && !invoice) return <div style={errStyle}>{error}</div>;
  if (!invoice) return <p style={{ color: 'var(--sj-text-subtle)' }}>Loading...</p>;

  const isOwner = invoice.userId === user?.oid;
  const editable = isOwner && invoice.status !== 'APPROVED' && !invoice.isLocked;

  async function saveMetadata(e: React.FormEvent) {
    e.preventDefault();
    if (!invoiceId) return;
    setError(null);
    setSavingMeta(true);
    try {
      const updated = await updateInvoice(invoiceId, {
        category,
        otherReason: category === 'Other' ? otherReason.trim() : undefined
      });
      setInvoice({ ...invoice!, ...updated });
    } catch (e2) {
      setError(String((e2 as Error).message ?? e2));
    } finally {
      setSavingMeta(false);
    }
  }

  async function pickReplacement(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const f = e.target.files?.[0];
    if (!f || !invoiceId) return;
    if (f.size > MAX_IMAGE_SIZE_BYTES) { setError('Image too large (max 10 MB).'); return; }
    if (!ALLOWED_CONTENT_TYPES.includes(f.type)) {
      setError(`Unsupported image type ${f.type || '(unknown)'}. Use JPEG, PNG, HEIC, or WebP.`);
      return;
    }
    setReplacingImage(true);
    try {
      const result = await replaceInvoiceImage(invoiceId, f.type);
      await uploadToS3(result.uploadUrl, f);
      // Re-fetch to get a fresh GET presign for the new key
      await refresh();
    } catch (e2) {
      setError(String((e2 as Error).message ?? e2));
    } finally {
      setReplacingImage(false);
      e.target.value = '';
    }
  }

  return (
    <div className="detail-grid">
      <div>
        <h1>Invoice</h1>
        <img
          src={invoice.imageUrl}
          alt="Invoice"
          className="invoice-image"
        />
        {editable && (
          <div style={{ marginTop: 12 }}>
            <label style={btn}>
              {replacingImage ? 'Uploading...' : 'Replace image'}
              {/* See InvoiceNew.tsx for why `capture` is omitted — same
                  reasoning applies to the replace-image flow. */}
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic,image/webp"
                style={{ display: 'none' }}
                onChange={pickReplacement}
                disabled={replacingImage}
              />
            </label>
          </div>
        )}
      </div>

      <aside className="section-card">
        <h2 style={{ fontSize: '1.4rem' }}>Details</h2>
        <dl style={dl}>
          <dt style={dt}>Status</dt><dd style={dd}>{invoice.status}{invoice.isLocked && ' · locked'}</dd>
          <dt style={dt}>Submitted by</dt><dd style={dd}>{invoice.userDisplayName || invoice.userEmail}</dd>
          <dt style={dt}>Created</dt><dd style={dd}>{formatTimestamp(invoice.createdAt)}</dd>
          <dt style={dt}>Updated</dt><dd style={dd}>{formatTimestamp(invoice.updatedAt)}</dd>
          {invoice.approvedAt && (<><dt style={dt}>Approved</dt><dd style={dd}>{formatTimestamp(invoice.approvedAt)}</dd></>)}
        </dl>

        {editable ? (
          <form onSubmit={saveMetadata} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
            <label style={label}>
              Category
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as InvoiceCategory)}
                style={input}
              >
                {INVOICE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            {category === 'Other' && (
              <label style={label}>
                Reason
                <input
                  type="text"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  maxLength={200}
                  style={input}
                />
              </label>
            )}
            <button type="submit" disabled={savingMeta} style={btnPrimary}>
              {savingMeta ? 'Saving...' : 'Save changes'}
            </button>
          </form>
        ) : (
          <p style={{ color: 'var(--sj-text-subtle)', marginTop: 16 }}>
            {invoice.status === 'APPROVED'
              ? 'Approved invoices cannot be edited.'
              : 'You can only edit invoices you uploaded.'}
          </p>
        )}

        {error && <p style={{ ...errStyle, marginTop: 12 }}>{error}</p>}
      </aside>
    </div>
  );
}

const label: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--sj-text-dark)'
};

const input: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--sj-line-strong)',
  borderRadius: 'var(--radius-xs)'
};

const btn: React.CSSProperties = {
  display: 'inline-block',
  padding: '10px 16px',
  fontSize: 14,
  fontWeight: 600,
  background: 'var(--sj-paper)',
  color: 'var(--sj-text-dark)',
  border: '1px solid var(--sj-line-strong)',
  borderRadius: 999,
  cursor: 'pointer'
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: 'var(--sj-crimson)',
  color: '#fff',
  borderColor: 'var(--sj-crimson)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase'
};

const dl: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  rowGap: 8,
  columnGap: 14,
  fontSize: 14,
  margin: '8px 0 0'
};

const dt: React.CSSProperties = {
  color: 'var(--sj-text-muted)',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: 11,
  letterSpacing: '0.1em',
  alignSelf: 'center'
};

const dd: React.CSSProperties = {
  margin: 0,
  color: 'var(--sj-text-dark)'
};

const errStyle: React.CSSProperties = {
  background: 'var(--sj-danger-bg)',
  border: '1px solid var(--sj-danger-line)',
  color: 'var(--sj-danger-fg)',
  padding: 12,
  borderRadius: 'var(--radius-xs)'
};
